/* The calculators.
 *
 * Both live-update as you type: an answer that only exists after pressing a
 * button is a form, not an instrument. Validation is stated inline under the
 * panel rather than in an alert(), which on a phone is a modal interruption
 * for a typo.
 *
 * The arithmetic is ported unchanged from the live tools so a number worked
 * out here matches one worked out there. Everything is done in metric
 * internally and converted only for display, so switching units can never
 * change the answer.
 */
(function () {
  'use strict';

  var L_PER_GAL = 3.785411784;   // US liquid gallon
  var CM_PER_IN = 2.54;
  var KG_PER_LB = 0.45359237;

  function num(el) {
    var v = parseFloat(el && el.value);
    return isFinite(v) ? v : NaN;
  }

  function round(n, dp) {
    var f = Math.pow(10, dp);
    return Math.round(n * f) / f;
  }

  /* Trailing zeros read as false precision on a big number and as missing
     precision on a small one, so the scale decides. */
  function pretty(n) {
    if (!isFinite(n)) return '0';
    var abs = Math.abs(n);
    var dp = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
    return round(n, dp).toLocaleString(undefined, {
      minimumFractionDigits: 0, maximumFractionDigits: dp
    });
  }

  function setText(root, key, value) {
    var el = root.querySelector('[data-out="' + key + '"]');
    if (el) el.textContent = value;
  }

  function fail(root, message) {
    var err = root.querySelector('[data-err]');
    if (!err) return;
    if (message) {
      err.textContent = message;
      err.hidden = false;
    } else {
      err.textContent = '';
      err.hidden = true;
    }
  }

  // ---- volume --------------------------------------------------------------
  function volume(root) {
    var form = root.querySelector('form');
    var imperialUnits = form.querySelector('input[name="units"]:checked').value === 'imperial';
    var shape = form.querySelector('input[name="shape"]:checked').value;

    // labels follow the chosen units, never the store
    var lin = imperialUnits ? 'in' : 'cm';
    var vol = imperialUnits ? 'gal' : 'L';
    var mass = imperialUnits ? 'lb' : 'kg';
    root.querySelectorAll('[data-unit]').forEach(function (u) { u.textContent = lin; });
    setText(root, 'volume-unit', vol);
    setText(root, 'capacity-unit', vol);
    setText(root, 'weight-unit', mass);
    setText(root, 'salt-unit', mass);

    // shape decides which fields are even asked for
    var isCyl = shape === 'cylinder';
    var isCube = shape === 'cube';
    var show = function (name, on) {
      var f = form.querySelector('[data-for="' + name + '"]');
      if (f) f.hidden = !on;
    };
    show('width', !isCyl);
    show('length', !isCyl && !isCube);
    show('diameter', isCyl);
    var wLabel = form.querySelector('[data-label="width"]');
    if (wLabel) wLabel.textContent = isCube ? 'Side' : 'Width';

    var toCm = function (v) { return imperialUnits ? v * CM_PER_IN : v; };
    var heightCm = toCm(num(form.querySelector('[name="height"]')));
    var fillCm = toCm(num(form.querySelector('[name="fill"]')));

    var widthCm = toCm(num(form.querySelector('[name="width"]')));
    var lengthCm = toCm(num(form.querySelector('[name="length"]')));
    var diaCm = toCm(num(form.querySelector('[name="diameter"]')));

    var needed = isCyl ? [diaCm, heightCm, fillCm]
      : isCube ? [widthCm, heightCm, fillCm]
        : [widthCm, lengthCm, heightCm, fillCm];
    if (needed.some(function (v) { return !isFinite(v) || v <= 0; })) {
      return fail(root, 'Fill in every measurement with a number above zero.');
    }
    if (fillCm > heightCm) {
      return fail(root, 'Water depth cannot be greater than the tank height.');
    }
    fail(root, '');

    // metres, exactly as the original
    var wM = widthCm * 0.01, lM = lengthCm * 0.01, hM = heightCm * 0.01;
    var fM = fillCm * 0.01, rM = (diaCm * 0.01) / 2;

    var volumeM3, capacityM3;
    if (isCube) {
      volumeM3 = wM * wM * fM;
      capacityM3 = wM * wM * hM;
    } else if (isCyl) {
      volumeM3 = Math.PI * rM * rM * fM;
      capacityM3 = Math.PI * rM * rM * hM;
    } else {
      volumeM3 = wM * lM * fM;
      capacityM3 = wM * lM * hM;
    }

    var litres = volumeM3 * 1000;
    var capacityL = capacityM3 * 1000;
    var percent = capacityL > 0 ? (litres / capacityL) * 100 : 0;
    var weightKg = litres * 1.0234;      // saltwater at 35 ppt
    var saltKg = (litres * 35) / 1000;

    var v = function (l) { return imperialUnits ? l / L_PER_GAL : l; };
    var m = function (kg) { return imperialUnits ? kg / KG_PER_LB : kg; };

    setText(root, 'volume', pretty(v(litres)));
    setText(root, 'capacity', pretty(v(capacityL)));
    setText(root, 'percent', pretty(percent));
    setText(root, 'weight', pretty(m(weightKg)));
    setText(root, 'salt', pretty(m(saltKg)));

    var bar = root.querySelector('[data-fill]');
    if (bar) bar.style.height = Math.max(0, Math.min(100, percent)) + '%';
    var gauge = root.querySelector('[data-fill-label]');
    if (gauge) gauge.setAttribute('aria-label', pretty(percent) + '% full');
  }

  // ---- salinity ------------------------------------------------------------
  function salinity(root) {
    var form = root.querySelector('form');
    var imperialUnits = form.querySelector('input[name="units"]:checked').value === 'imperial';
    var vol = imperialUnits ? 'gal' : 'L';
    root.querySelectorAll('[data-unit]').forEach(function (u) { u.textContent = vol; });
    ['ro-unit', 'from-unit', 'final-unit'].forEach(function (k) { setText(root, k, vol); });

    var volume = num(form.querySelector('[name="volume"]'));
    var current = num(form.querySelector('[name="current"]'));
    var target = num(form.querySelector('[name="target"]'));

    if (!isFinite(volume) || !isFinite(current) || !isFinite(target) || volume <= 0) {
      return fail(root, 'Fill in the volume and both readings.');
    }
    if (target < 1) {
      return fail(root, 'Target specific gravity cannot be below 1.000, which is pure water.');
    }
    if (current <= target) {
      return fail(root, 'Current specific gravity has to be higher than the target. To raise salinity you add salt, not water.');
    }
    fail(root, '');

    var ro = volume * (current - target) / (target - 1.000);
    var final = volume + ro;
    var ratio = (ro / volume) * 100;

    setText(root, 'ro', pretty(ro));
    setText(root, 'from', pretty(volume));
    setText(root, 'from-sg', current.toFixed(3));
    setText(root, 'final', pretty(final));
    setText(root, 'final-sg', target.toFixed(3));
    setText(root, 'ratio', pretty(ratio));
  }

  var RUN = { volume: volume, salinity: salinity };

  document.querySelectorAll('[data-ak-calc]').forEach(function (root) {
    var run = RUN[root.getAttribute('data-ak-calc')];
    if (!run) return;
    var form = root.querySelector('form');
    if (!form) return;

    var go = function () { run(root); };
    form.addEventListener('input', go);
    form.addEventListener('change', go);
    // nothing to submit: the answer is already on screen
    form.addEventListener('submit', function (e) { e.preventDefault(); });
    go();
  });
})();
